/** @prettier */

import {Grid, Stack, Box, Container} from '@mui/material';
import {useState, useEffect} from 'react';
import {useLoaderData, useNavigate, useParams} from 'react-router-dom';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import EnhancedTable from './DataTable';
import {PrimaryButton} from '../../component/BasicButton';
//API import
import {
  getCompositionsGateway,
  getCompositionsRTUnit,
  getCompositionsSitesBySiteID,
  getReportPeriodic,
  postReportPeriodic,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {limit} from '../../define';
import {
  convertByteIntoKiloByte,
  convertByteIntoMegaByte,
} from '../../functions/convertNumber';

/**
 * QtonとHydrolution PROの存在状況を表すインターフェース。
 * ユニットが存在していればtrue、存在しないか権限がなければfalse。
 */
export interface qtonHydrolutionPROExistence {
  /**
   * Qtonの登録状況。
   */
  hasQton: boolean;
  /**
   * Hydrolution PROの登録状況。
   */
  hasHydrolutionPRO: boolean;
}

type LoaderReturnType = {
  timeZone: string; //タイムゾーン
  reportList: apiIF.res.getReportPeriodic; //定期レポート一覧
  showCautionMessage: boolean;
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  const promises = [];
  //サイトの情報を取得
  promises.push(getCompositionsSitesBySiteID(params.siteID));
  //日報の一覧を取得
  promises.push(getReportPeriodic(params.siteID));
  //サイト内のゲートウェイ情報を取得
  promises.push(
    getCompositionsGateway(params.siteID, {
      'fields': 'register-number',
      'max-result': limit.REGISTRATION_NUM_GW_MAX,
    })
  );

  try {
    const response = await Promise.all(promises);
    const siteInfo = response[0] as apiIF.res.getCompositionsSitesBySiteID;
    const reportInfo = response[1] as apiIF.res.getReportPeriodic;
    const gwInfo = response[2] as apiIF.res.getCompositionsGateway;

    //admin以外がアクセスしたらリダイレクト
    if (siteInfo['site-authority'] !== 'admin') {
      throw new Error('Insufficient permission');
    }

    //ファイル名称を変更する
    /** 一時レポート情報変数 */
    const tempReportInfo = reportInfo;
    //レポート種別毎に処理
    for (const key in reportInfo) {
      //レポート一時変数にファイル名を置き換えたオブジェクトを保存
      tempReportInfo[key as keyof typeof reportInfo] = reportInfo[
        key as keyof typeof reportInfo
      ].map(fileData => {
        //pdfファイル名から情報を抽出
        const [siteID, unitType, unitName, date] =
          fileData['file-name'].split('_');

        //ファイル名称を作成
        const fileName = `${date.split('.')[0]}_${
          siteInfo['site-name']
        }_${unitName}.pdf`;

        return {
          'file-name': fileName,
          'file-size': fileData['file-size'],
          'time-stamp': fileData['time-stamp'],
        };
      });
    }

    /** 熱源機登録状況 */
    const result: qtonHydrolutionPROExistence = {
      hasQton: false,
      hasHydrolutionPRO: false,
    }; //初期値

    const getRtUnitPromises = [];

    //QtonとHydrolution PROの登録状況の更新と定期レポート関連の情報の取得を行う。
    for (const info of gwInfo['gateway-informations']) {
      if (info['register-number'] === undefined) continue;
      //熱源機登録状況の更新
      if (Number(info['register-number']['qton']) > 0) {
        result.hasQton = true;
      }
      if (Number(info['register-number']['chiller']) > 0) {
        result.hasHydrolutionPRO = true;
      }
      //定期レポート関連の情報取得
      //QtonもHydrolution PROも登録されていないゲートウェイの場合、情報は取得しない。
      if (
        Number(info['register-number']['qton']) < 0 &&
        Number(info['register-number']['chiller']) < 0
      ) {
        continue;
      }
      getRtUnitPromises.push(
        getCompositionsRTUnit(params.siteID, info['gateway-id'], {
          'fields': 'unit-type,periodic-report',
          'max-result': 400,
        })
      );
    }

    //Q-ton,Hydrolution PROがともに未登録ならリダイレクト
    const unregistered =
      result.hasQton === false && result.hasHydrolutionPRO === false;

    if (unregistered) {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    /** 注意メッセージ表示/非表示 */
    let showCautionMessage = false;

    const getRtUnitResponses = await Promise.all(getRtUnitPromises);

    //ユニット毎にレポート生成の有効/無効を確認
    let isActiveReport = false;
    for (const gwInfo of getRtUnitResponses) {
      //レポート生成有効のユニットが見つかったらループ終了
      if (isActiveReport === true) {
        break;
      }
      for (const unitInfo of gwInfo['unit-informations']) {
        if (
          (unitInfo['unit-type'] === 'QtonOpen' ||
            unitInfo['unit-type'] === 'QtonClose' ||
            unitInfo['unit-type'] === 'Chiller') &&
          unitInfo['periodic-report'] !== undefined
        ) {
          if (unitInfo['periodic-report']['report-generation'] === 'active') {
            isActiveReport = true;
            break;
          }
        }
      }
    }

    //全てのユニットがレポート生成無効となっている場合は注意メッセージ表示
    if (isActiveReport === false) {
      showCautionMessage = true;
    }

    return {
      timeZone: siteInfo.timezone,
      reportList: tempReportInfo,
      showCautionMessage: showCautionMessage,
    };
  } catch {
    return undefined;
  }
}

/**
 * 定期レポート一覧
 * @returns JSX
 */
const PeriodicReport = () => {
  const trans = useTranslation().t; //翻訳関数
  const siteInfo = useLoaderData() as LoaderReturnType | undefined;
  //パスパラメータ取得
  const {siteID} = useParams();

  const [quarterlyDownloadList, setQuarterlyDate] = useState<readonly string[]>(
    []
  ); //ダウンロードする四半期レポート
  const [quarterlySize, setQuarterlySize] = useState(0); //ダウンロードする四半期レポートのサイズ
  const [monthlyDownloadList, setMonthlyDate] = useState<readonly string[]>([]); //ダウンロードする月次レポート
  const [monthlySize, setMonthlySize] = useState(0); //ダウンロードする月次レポートのサイズ
  const [downloadBtnDisabled, setDownloadBtnDisabled] = useState(true); //Downloadボタン非表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ
  const navigate = useNavigate();

  //Lambdaが1回に処理できる上限が512MBなので制限を設ける
  useEffect(() => {
    if (
      convertByteIntoMegaByte(quarterlySize + monthlySize, 1) >
      limit.DOWNLOAD_FILE_SIZE_MAX
    ) {
      setDownloadBtnDisabled(true);
    } else if (quarterlySize + monthlySize === 0) {
      setDownloadBtnDisabled(true);
    } else {
      setDownloadBtnDisabled(false);
    }
  }, [quarterlySize, monthlySize, siteInfo]);

  /**
   * Downloadボタン押下時処理
   * @returns
   */
  const downloadReport = () => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    setAPIWait(true);
    setDownloadBtnDisabled(true);

    //定期レポートダウンロード
    const downloadList: apiIF.reqParameters.postReportPeriodic = {};
    if (quarterlyDownloadList.length > 0) {
      downloadList['quarterly-reports'] = [...quarterlyDownloadList];
    }
    if (monthlyDownloadList.length > 0) {
      downloadList['monthly-reports'] = [...monthlyDownloadList];
    }
    postReportPeriodic(siteID, downloadList)
      .then(response => {
        const link = document.createElement('a');
        link.href = response['download-path'];
        link.click();
        setAPIWait(false);
        setDownloadBtnDisabled(false);
      })
      .catch(err => {
        setAPIWait(false);
        setDownloadBtnDisabled(false);
      });
  };

  //タイムゾーン、レポート一覧を取得できない場合
  if (siteInfo === undefined) {
    return <></>;
  }
  return (
    <Container fixed>
      <Grid container rowSpacing={5}>
        {/** タイトル、説明 */}
        <Grid item xs={12}>
          <h1>{trans('PeriodicReport.title')}</h1>
          <h2>{trans('PeriodicReport.instruction')}</h2>
        </Grid>
        {/** レポート無効の警告 */}
        {siteInfo.showCautionMessage === true ? (
          <Grid item xs={12}>
            <h2 className="serviceInfo__periodicReport__warning">
              {trans('PeriodicReport.reportEnable1')}
            </h2>

            <h2 className="serviceInfo__periodicReport__warning">
              {trans('PeriodicReport.reportEnable2')}
            </h2>
          </Grid>
        ) : (
          <></>
        )}
        {/** 設定ボタン */}
        <Grid container justifyContent="flex-end">
          <PrimaryButton
            onClick={() => {
              navigate(
                `/pages/my-site/site-detail/${siteID}/periodic-report-setting`
              );
            }}
            buttonText={trans('PeriodicReport.buttonTxt.setting')}
            id="mv-reportSettingBtn"
          />
        </Grid>
        {/** レポート一覧 */}
        <Grid item xs={12} container justifyContent="center" spacing={3}>
          <Grid item xs={12} lg={6}>
            <Stack>
              <h3>{trans('PeriodicReport.heading.monthly')}</h3>
              <EnhancedTable
                tableData={siteInfo.reportList['monthly-reports']}
                tableClassName="serviceInfo__dailyReportData__report--monthly"
                setRetData={setMonthlyDate}
                setSize={setMonthlySize}
                timeZone={siteInfo.timeZone}
                id="monthlyData"
                orderBy="timestamp"
              />
            </Stack>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Stack>
              <h3>{trans('PeriodicReport.heading.quarterly')}</h3>
              <EnhancedTable
                tableData={siteInfo.reportList['quarterly-reports']}
                /** 四半期は月次と同じ大きさで表示するためmonthlyのクラスを使用する */
                tableClassName="serviceInfo__dailyReportData__report--monthly"
                setRetData={setQuarterlyDate}
                setSize={setQuarterlySize}
                timeZone={siteInfo.timeZone}
                id="quarterlyData"
                orderBy="timestamp"
              />
            </Stack>
          </Grid>
        </Grid>
        <Grid item xs={12} container justifyContent="flex-end">
          <Grid item xs="auto">
            <Stack spacing={1}>
              <PrimaryButton
                onClick={downloadReport}
                buttonText={trans('DailyReport.buttonTxt.download')}
                id="mv-reportDataDownloadBtn"
                disabled={downloadBtnDisabled}
                className="serviceInfo__dailyReportData__button"
                apiWait={apiWait}
              />
              <Box>
                {trans('DailyReport.totalSize', {
                  currentSize: convertByteIntoKiloByte(
                    quarterlySize + monthlySize,
                    1
                  ),
                  maximumSize: limit.DOWNLOAD_FILE_SIZE_MAX,
                })}
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};
export default PeriodicReport;
